import { FormControl, Grid, InputLabel, MenuItem, TextField, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import * as yup from "yup";
import { useMobileView } from '../../../../../Hooks/useMobileView';
import { SpecializationView, useSpecializations } from '../../../../../Hooks/useSpecializations';
import { useAppDispatch } from '../../../../../Redux/store';
import { ExpertiseSchema } from '../../../../../Schemas/ResumeSchemas';
import { KaziSpecializations } from '../../../../../Shared Components/Kazi/KaziSpecializations';
import { setNextDisabled } from '../../Slices/TalentProfileCreationSlice';

interface ExpertiseForm {
    PrimaryExpertise: string;
    ExpertiseLevel: string;
    SecondaryExpertise: string;
    TertiaryExpertise: string;
}

interface IProps {
    expertiseForm: UseFormReturn<yup.InferType<typeof ExpertiseSchema>>
}

export const Expertise = ({ expertiseForm }: IProps) => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const {
        control,
        formState: { isValid },
        watch,
        getValues
    } = expertiseForm
    const { getExcludeList } = useSpecializations({watch, getValues, view: SpecializationView.ProfileCreation});
    /* ******************** Variables ******************** */
    const { flexDirection } = useMobileView();
    /* ******************** Functions ******************** */
    /* ******************** Effects ******************** */
    useEffect(() => {
        console.log(getValues())
        isValid ? dispatch(setNextDisabled(false)) : dispatch(setNextDisabled(true));
    }, [isValid])
    /* ******************** JSX ******************** */
    return (
        <Grid container gap={'20px'} width={"100%"}>
            <Typography variant='lora-title-large' color={'primary'} fontWeight={'bold'}>Add your expertise</Typography>
            <Grid item display={"flex"} flexDirection={flexDirection} gap={"12px"} width={"100%"}>
            </Grid>
            <Grid item display={"flex"} flexDirection={flexDirection} gap={"12px"} width={"100%"}>
                <Grid item display={"flex"} flexDirection={"column"} width={"100%"}>
                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>Primary expertise</Typography>  
                    <Controller
                        name="primaryExpertise"
                        control={control}
                        rules={{required: true}}
                        render={({ field }) =>( 
                            <KaziSpecializations 
                                {...field}
                                excludeList={getExcludeList(field.name)}
                                placeholder='Primary Expertise'
                                fullWidth
                                textFieldProps={
                                    {
                                        sx: {
                                            minWidth: "270px"
                                        }
                                    }
                                }
                            /> 
                        )}
                    />   
                </Grid>
                <Grid item display={"flex"} flexDirection={"column"} width={"100%"}>
                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>Expertise level</Typography>  
                    <Controller
                        name="expertiseLevel"
                        control={control}
                        rules={{required: true}}
                        render={({ field }) =>( 
                            <FormControl>   
                                <InputLabel sx={{opacity: 1}}>{field.value ?"":"Expertise level"}</InputLabel>                   
                                <TextField
                                    {...field}
                                    // error={hasErrors(field.name, errors)}
                                    select
                                    fullWidth
                                    sx={{
                                        minWidth: "270px"
                                    }}
                                >
                                    {
                                        [
                                            "Entry-level ( 0-2 years)", 
                                            "Mid-level (3-5 years)" , 
                                            "Senior-level (6+ years)",
                                            "Flexible"
                                        ].map((level) => (
                                            <MenuItem key={level} value={level}>
                                                {level}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                            </FormControl>
                        )}
                    />   
                </Grid>
            </Grid>
            <Grid item display={"flex"} flexDirection={flexDirection} gap={"12px"} width={"100%"}>
                <Grid item display={"flex"} flexDirection={"column"} width={"100%"}>
                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>Secondary expertise (optional)</Typography>  
                    <Controller
                        name="secondaryExpertise"
                        control={control}
                        rules={{required: true}}
                        render={({ field }) =>(                             
                            <KaziSpecializations 
                                {...field}
                                excludeList={getExcludeList(field.name)}
                                placeholder='Secondary Expertise'
                                fullWidth
                                textFieldProps={
                                    {
                                        sx: {
                                            minWidth: "270px"
                                        }
                                    }
                                }
                            /> 
                        )}
                    />   
                </Grid>
                <Grid item display={"flex"} flexDirection={"column"} width={"100%"}>
                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>Tertiary expertise (optional)</Typography>  
                    <Controller
                        name="tertiaryExpertise"
                        control={control}
                        rules={{required: true}}
                        render={({ field }) =>(                             
                            <KaziSpecializations 
                                {...field}
                                excludeList={getExcludeList(field.name)}
                                placeholder='Tertiary Expertise'
                                fullWidth
                                textFieldProps={
                                    {
                                        sx: {
                                            minWidth: "270px"
                                        }
                                    }
                                }
                            /> 
                        )}
                    />   
                </Grid>
            </Grid>
        </Grid>
    )
}